export type AppointmentStatus =
  | 'scheduled'
  | 'rescheduled'
  | 'completed'
  | 'no_show'
  | 'cancelled';

export type CommKind =
  | 'scheduled'
  | 'tomorrow'
  | 'today'
  | 'followup'
  | 'cancelled'
  | 'no_show_rebook';

export type AppointmentSource = 'compliance' | 'renter_self' | 'agent';

export type CommStatus = 'pending' | 'sent' | 'failed';

export interface AppointmentCommunicationSummary {
  id: number;
  comm_kind: CommKind;
  status: CommStatus;
  attempts: number;
  error_detail: string | null;
  sent_at: string | null;
  conversation: number | null;
  message: number | null;
  cert_member_name: string | null;
}

export interface UnitOption {
  id: number;
  name: string;
  building: number | null;
  building_name: string;
  label: string;
}

export type AppointmentMessageKind =
  | 'scheduled'
  | 'tomorrow'
  | 'today'
  | 'followup'
  | 'cancelled'
  | 'no_show';

/** Per-type message override. Empty fields fall back to the generic
 * property/global template at send time. */
export interface AppointmentCategoryMessage {
  kind: AppointmentMessageKind;
  subject: string | null;
  email_text: string | null;
  sms_text: string | null;
  /** Rendered against sample values; what the renter actually receives. */
  email_preview?: string;
  sms_preview?: string;
  /** Which generator produced the stored text. */
  source?: 'llm' | 'fallback';
  generated_at?: string | null;
}

/** One AI-drafted message, including the rendered sample for review. */
export interface CategoryMessageDraft {
  kind: AppointmentMessageKind;
  subject: string;
  email_text: string;
  sms_text: string;
  email_preview: string;
  sms_preview?: string;
}

export interface DraftMessagesResponse {
  drafts: CategoryMessageDraft[];
  source: 'llm' | 'fallback';
  detail: string | null;
}

export interface AppointmentCategory {
  id: number;
  name: string;
  description: string | null;
  property: number | null;
  is_active: boolean;
  default_duration_minutes: number;
  default_location: string | null;
  /** Property-local IANA timezone (e.g. "America/New_York") for the queried
   * property — used to interpret/display appointment times in property-local
   * time, matching renter communications. */
  timezone?: string | null;
  renter_bookable?: boolean;
  renter_cancel_cutoff_hours?: number;
  booking_instructions?: string | null;
  /** How many of the property's availability windows name this type.
   * Null/absent when the row did not come from the property category list. */
  open_windows?: number | null;
  /** True when renters can actually see and book it: bookable *and* at least
   * one window names it. Null when unknown. */
  renter_ready?: boolean | null;
  messages: AppointmentCategoryMessage[];
}

export interface AppointmentHostBrief {
  id: number;
  name: string;
  user: number;
  is_active: boolean;
}

export interface ConflictingAppointment {
  id: number;
  category_name: string;
  start_at: string;
  end_at: string;
}

export interface Appointment {
  id: number;
  certification: number;
  property: number;
  category: AppointmentCategory;
  host?: AppointmentHostBrief | null;
  source?: AppointmentSource;
  source_label?: string;
  start_at: string;
  end_at: string;
  location: string | null;
  notes: string | null;
  status: AppointmentStatus;
  outcome_notes?: string | null;
  outcome_marked_at?: string | null;
  followup_required?: boolean;
  rescheduled_from: number | null;
  units?: UnitOption[];
  duration_minutes?: number | null;
  communications: AppointmentCommunicationSummary[];
  created: string;
  updated: string;
  /** Property-local IANA timezone the times should be rendered in — the same
   * zone the renter communications use. */
  timezone?: string | null;
  conflicting_appointments?: ConflictingAppointment[];
  duplicate?: boolean;
}

export interface ScheduleAppointmentPayload {
  certification: number;
  category: number;
  start_at: string;
  /** Derived from `duration_minutes` / the type default when omitted. */
  end_at?: string;
  /** 30 / 60 / 90 / 120 or the type default (R10). */
  duration_minutes?: number | null;
  location?: string | null;
  notes?: string | null;
  /** Omit to let the backend pick the least-loaded free host (R9). */
  host?: number | null;
  /** Units attached for display/tracking only (R13/B11). */
  units?: number[];
}

export interface ReschedulePayload {
  new_start_at: string;
  new_end_at?: string;
  duration_minutes?: number | null;
  location?: string | null;
  notes?: string | null;
  new_host?: number | null;
}

export interface OutcomePayload {
  outcome: 'completed' | 'no_show';
  outcome_notes?: string | null;
  followup_required?: boolean;
  /** No-show only: issue a booking invite for the same type + send the rebook link. */
  invite_to_rebook?: boolean;
}

// ---------------------------------------------------------------------
// Booking invites (ENG-3895 follow-up) — the human decision that lets a
// household self-book. One active invite per certification.
// ---------------------------------------------------------------------

export type BookingInviteState = 'active' | 'used' | 'revoked' | 'expired';

export type BookingInviteSource = 'manual' | 'agent' | 'rebook';

export interface BookingInviteNotificationStatus {
  sent?: Array<{ cert_member?: number; channel?: number }>;
  skipped?: Array<{ cert_member?: number; reason: string }>;
  failed?: Array<{ cert_member?: number; channel?: number; error?: string }>;
}

export interface BookingInvite {
  id: number;
  certification: number;
  property: number;
  state: BookingInviteState;
  source: BookingInviteSource;
  categories: Array<{ id: number; name: string }>;
  /** The person the invite is scoped to; null = any available host. */
  host?: AppointmentHostBrief | null;
  invited_by: number | null;
  invited_by_name: string | null;
  invited_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  revoked_by: number | null;
  revoked_by_name: string | null;
  appointment: number | null;
  appointment_start_at: string | null;
  note: string | null;
  notification_status: BookingInviteNotificationStatus;
  duration_minutes?: number | null;
  units?: UnitOption[];
}

export interface BookingInviteListResponse {
  results: BookingInvite[];
  active: BookingInvite | null;
}

export interface CreateBookingInvitePayload {
  certification: number;
  categories?: number[];
  /** Pin the invite to one host; omitted/null = any available host. */
  host?: number | null;
  /** Omit for the default (14 days); `null` for no expiry. */
  expires_at?: string | null;
  note?: string | null;
  notify?: boolean;
  /** 30 / 60 / 90 / 120; omitted = the type default (R10). */
  duration_minutes?: number | null;
  /** Units carried onto the appointment the renter books (R13/B11). */
  units?: number[];
}

// ---------------------------------------------------------------------
// ENG-3895 — hosts, availability, calendar range
// ---------------------------------------------------------------------

export interface HostUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}

export interface AppointmentHost {
  id: number;
  property: number;
  user: HostUser;
  name: string;
  display_name: string | null;
  is_active: boolean;
  accepts_renter_bookings: boolean;
  slot_interval_minutes: number;
  buffer_minutes: number;
  min_notice_hours: number;
  max_advance_days: number;
  default_location: string | null;
  upcoming_count: number;
  rule_count: number;
  created: string;
  updated: string;
}

export type HostSettingsPayload = Partial<
  Pick<
    AppointmentHost,
    | 'display_name'
    | 'accepts_renter_bookings'
    | 'slot_interval_minutes'
    | 'buffer_minutes'
    | 'min_notice_hours'
    | 'max_advance_days'
    | 'default_location'
    | 'is_active'
  >
>;

export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export const WEEKDAY_LABELS: Record<Weekday, string> = {
  0: 'Mon',
  1: 'Tue',
  2: 'Wed',
  3: 'Thu',
  4: 'Fri',
  5: 'Sat',
  6: 'Sun',
};

export interface HostAvailabilityRule {
  id: number;
  host: number;
  weekday: Weekday;
  weekday_label: string;
  /** "HH:MM:SS" property-local wall-clock */
  start_time: string;
  end_time: string;
  effective_from: string | null;
  effective_to: string | null;
  category_allowlist: number[];
}

export interface HostAvailabilityRuleInput {
  weekday: Weekday;
  start_time: string;
  end_time: string;
  effective_from?: string | null;
  effective_to?: string | null;
  category_allowlist?: number[];
}

export type OverrideKind = 'block' | 'open';

export interface HostAvailabilityOverride {
  id: number;
  host: number;
  date: string;
  kind: OverrideKind;
  start_time: string | null;
  end_time: string | null;
  reason: string | null;
}

export interface HostAvailabilityOverrideInput {
  date: string;
  kind: OverrideKind;
  start_time?: string | null;
  end_time?: string | null;
  reason?: string | null;
}

export interface Slot {
  start_at: string;
  end_at: string;
  host_id: number;
  host_name: string;
}

export interface SlotDay {
  date: string;
  slots: Slot[];
}

export interface EligibleUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  already_host: boolean;
}

export interface CalendarRow {
  id: number;
  certification: number;
  log_number: string | null;
  household_name: string | null;
  category_name: string;
  start_at: string;
  end_at: string;
  start_local: string;
  status: AppointmentStatus;
  location: string | null;
  host_id: number | null;
  host_name: string | null;
  source: AppointmentSource;
  followup_required: boolean;
  /** Present on my-calendar rows only. */
  property?: number;
  property_name?: string;
  property_slug?: string;
  timezone?: string;
  /** Units attached to the appointment; my-calendar returns them expanded. */
  units?: UnitOption[];
}

export interface AvailabilityBand {
  host_id: number;
  host_name: string;
  date: string;
  start_at: string;
  end_at: string;
}

/** Time the host cannot be booked because her own synced calendar is busy
 * (ENG-4235). Deliberately interval-only: Thor never sends the provider,
 * account, title, or attendees, and this surface must not ask for them. */
export interface UnavailableBand {
  start_at: string;
  end_at: string;
}

export interface CalendarRangeResponse {
  property: number;
  view: 'range';
  timezone: string;
  start: string;
  end: string;
  appointments: CalendarRow[];
  availability: AvailabilityBand[];
  hosts: AppointmentHost[];
}

export interface MyCalendarHostProfile {
  id: number;
  property: number;
  property_name: string;
  property_slug: string;
  timezone: string;
  is_active: boolean;
}

export interface MyCalendarResponse {
  user: number;
  start: string;
  end: string;
  appointments: CalendarRow[];
  /** Absent on a Thor that predates ENG-4235. */
  unavailable?: UnavailableBand[];
  host_profiles: MyCalendarHostProfile[];
}

export const SOURCE_LABELS: Record<AppointmentSource, string> = {
  compliance: 'Staff',
  renter_self: 'Renter-booked',
  agent: 'Agent',
};

export const APPOINTMENT_STATUS_LABELS: Record<AppointmentStatus, string> = {
  scheduled: 'Scheduled',
  rescheduled: 'Rescheduled',
  completed: 'Completed',
  no_show: 'No Show',
  cancelled: 'Cancelled',
};

export const COMM_KIND_LABELS: Record<CommKind, string> = {
  scheduled: 'Scheduled notice',
  tomorrow: 'Day-before reminder',
  today: 'Day-of reminder',
  followup: 'Follow-up',
  cancelled: 'Cancellation notice',
  no_show_rebook: 'Rebook nudge',
};

/** One host's connected calendar (ENG-4134). `/calendar-sync/status/`. */
export type CalendarConnectionState =
  | 'connected'
  | 'stale'
  | 'reauth_required'
  | 'disconnected';

export interface CalendarConnectionStatus {
  id: number;
  provider: 'microsoft' | 'google' | 'google_workspace';
  provider_family: 'microsoft' | 'google';
  provider_label: string;
  external_account_email: string;
  status: CalendarConnectionState;
  last_synced_at: string | null;
  last_error_code: string;
  reauth_required_at: string | null;
  connected_at: string | null;
}
